import axios from "axios";
import React, { useEffect, useState, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toZonedTime } from 'date-fns-tz';
import { api } from "ticketino-api-client";
import {
    GetOrders, GetCountOrders, GetOrdersOfBuyer, CountBuyerOrders, GetEventById,
    GetPdfTicketsOfOrderByOrderId, GetReceiptOfOrderByOrderId, GetTicketsOfOrder, GetTicketTypeById, GetAddressOfOrder, GetOrganizerProfile
} from "../../utils/api";
import { useLanguage } from "../../hooks/useLanguage";
import { useAuthGuard } from "../../hooks/useAuthGuard";
import { useOrder } from "../../hooks/useOrder";

import Header from "../components/Header";
import Footer from "../components/Footer";
import Loading from "../components/Loading";

import previewSvg from "../../images/icons/eye.svg";
import searchSvg from "../../images/icons/search.svg";
import newBookingSvg from "../../images/icons/new-booking.svg";

const Orders = () => {
    // Initialize buyerToken from localStorage so it's available immediately
    const [regularToken, setRegularToken] = useState(localStorage.getItem("token"));
    const [buyerToken, setBuyerToken] = useState(localStorage.getItem("buyerToken"));

    const organizerId = localStorage.getItem("loggedInOrganizerId");
    const [orders, setOrders] = useState(null);
    const [countOrders, setCountOrders] = useState(null);

    //const isAdmin = useMemo(() => organizerId === "2639969" || organizerId === "817192", [organizerId]);
    const isAdmin = useMemo(() => organizerId === "2639969", [organizerId]);

    const [organizerWOWId, setOrganizerWOWId] = useState(null);

    const [isLoading, setIsLoading] = useState(false);

    const [resources, setResources] = useState({});
    const { language } = useLanguage();

    const [filterText, setFilterText] = useState("");
    const [showGroupBookingOnly, setShowGroupBookingOnly] = useState(true); // Filter for orders with "group" tag
    const [showPastOrders, setShowPastOrders] = useState(true);
    // Add state for date filter
    const [filterDate, setFilterDate] = useState(""); // format: "YYYY-MM-DD" for input type="date"
    const [filterDateText, setFilterDateText] = useState(""); // for manual entry (DD.MM.YYYY)

    const timeZone = 'Europe/Zurich';
    const zurichDateNow = toZonedTime(new Date(), timeZone);
    const oneWeekFromNowDate = new Date(zurichDateNow.getTime() + 7 * 24 * 60 * 60 * 1000);

    let navigate = useNavigate();
    let baseUrl = process.env.REACT_APP_BASEURL_API;
    api.defaults.baseURL = baseUrl;

    const order = useOrder(baseUrl);

    useAuthGuard(language); // Check if user is authenticated

    // Remove adminNewBookingUsername from sessionStorage on mount
    useEffect(() => {
        sessionStorage.removeItem("adminNewBookingUsername");
        requestFormSettings();
    }, []);

    useEffect(() => {
        if (language) {
            requestResources();
        }
    }, [language]);

    useEffect(() => {
        if (buyerToken && organizerId && organizerWOWId) {

            if (isAdmin) {
                fetchOrders();
                fetchCountOrders();
            } else {
                fetchBuyerOrders();
                fetchCountBuyerOrders();
            }
        }
    }, [buyerToken, organizerId, isAdmin, organizerWOWId, showGroupBookingOnly]);

    // filterText change
    useEffect(() => {
        if (buyerToken && organizerId && organizerWOWId) {
            const handler = setTimeout(() => {
                if (isAdmin) {
                    fetchOrders(0, filterText);
                    fetchCountOrders(filterText);
                } else {
                    fetchBuyerOrders(0, filterText);
                    fetchCountBuyerOrders(filterText);
                }
            }, 400); // debounce
            return () => clearTimeout(handler);
        }
    }, [filterText, isAdmin, buyerToken, organizerId, organizerWOWId, showGroupBookingOnly]);

    const requestResources = async () => {
        await axios
            .get(`form/resources/${language}`)
            .then((res) => {
                setResources(res.data?.translation);
            })
            .catch((error) => console.error(error.response.data));
    };

    const requestFormSettings = async () => {
        await axios.get(`form/formsettings`).then((res) => {
            setOrganizerWOWId(res.data.organizerId);
        });
    };

    // Fetch tickets and organizer profile for all orders and attach to order objects
    const attachTicketsToOrders = async (ordersList) => {
        if (!ordersList) return [];
        const ordersWithDetails = await Promise.all(
            ordersList.map(async (order) => {
                try {
                    const tickets = await GetTicketsOfOrder(baseUrl, "buyerToken", buyerToken, order.id);
                    // Attach ticket type and event to each ticket using GetTicketTypeById and fetchEventById
                    const ticketsWithTicketType = await Promise.all(
                        (Array.isArray(tickets) ? tickets : []).map(async (tt) => {
                            try {
                                const ticketType = await GetTicketTypeById(baseUrl, "token", regularToken, tt.ticketTypeId);
                                let event = null;
                                try {
                                    if (ticketType && ticketType.eventId) {
                                        event = await fetchEventById(ticketType.eventId);
                                    }
                                } catch {
                                    event = null;
                                }
                                return { ...tt, ticketType: { ...ticketType, event } };
                            } catch {
                                return { ...tt, ticketType: null };
                            }
                        })
                    );

                    // Filter out orders that are not from WOW logic: at least one event with organizerId 2639969, and at least one ticketType.event exists
                    const hasValidEvent = ticketsWithTicketType.some(t => t.ticketType && t.ticketType.event);
                    const hasOrganizerWOW = ticketsWithTicketType.some(t => t.ticketType && t.ticketType.event && t.ticketType.event.organizerId == organizerWOWId);
                    if (!hasValidEvent || !hasOrganizerWOW) {
                        return null; // filter out this order
                    }

                    // Determine if all tickets are in the past, using Zurich timezone
                    const isPastOrder = ticketsWithTicketType.length > 0 && ticketsWithTicketType.every(t => {
                        if (!t.ticketType || !t.ticketType.end) return false;
                        const endDate = toZonedTime(new Date(t.ticketType.end), timeZone);
                        return endDate < zurichDateNow;
                    });
                    // Determine if all tickets are at least 1 week in the future (can also be more)
                    const isOneWeekBeforeEvent = ticketsWithTicketType.length > 0 && ticketsWithTicketType.every(t => {
                        if (!t.ticketType || !t.ticketType.end) return false;
                        const endDate = toZonedTime(new Date(t.ticketType.end), timeZone);
                        return endDate >= oneWeekFromNowDate;
                    });
                    // Fetch address for this order
                    let orderAddress = null;
                    try {
                        if (order.id) {
                            orderAddress = await GetAddressOfOrder(baseUrl, "buyerToken", buyerToken, order.id);
                        }
                    } catch {
                        orderAddress = null;
                    }
                    return { ...order, tickets: ticketsWithTicketType, orderAddress, isPastOrder, isOneWeekBeforeEvent };
                } catch {
                    return null; // filter out this order on error
                }
            })
        );
        // Filter out nulls (orders that didn't pass the filter)
        return ordersWithDetails.filter(Boolean);
    };

    // For admin
    // Update fetchOrders to attach tickets to each order
    const fetchOrders = async (numberToSkip = 0, filter = filterText) => {
        try {
            if (!buyerToken) {
                throw new Error("No buyerToken token found at fetchOrders");
            }

            setIsLoading(true);

            // Apply tag filter based on showGroupBookingOnly checkbox
            const filterTags = showGroupBookingOnly ? ["group"] : null;

            const data = await GetOrders(baseUrl, "buyerToken", buyerToken, organizerId, false, filter, numberToSkip, null, filterTags);
            const ordersWithTickets = await attachTicketsToOrders(data.orderList);

            // Sort orders by dateCreated descending (latest first)
            const sortedOrders = ordersWithTickets.sort((a, b) => new Date(b.dateCreated) - new Date(a.dateCreated));

            if (numberToSkip && orders) {
                setOrders(prevOrders => [...prevOrders, ...sortedOrders]);
            } else {
                setOrders(sortedOrders);
            }

            setIsLoading(false);
        } catch (error) {
            setIsLoading(false);
            console.error('Error fetching orders:', error);
        }
    };

    // For common buyer
    const fetchBuyerOrders = async (numberToSkip = 0, filter = filterText) => {
        try {
            if (!buyerToken) {
                throw new Error("No buyerToken token found at fetchBuyerOrders");
            }
            setIsLoading(true);

            // Apply tag filter based on showGroupBookingOnly checkbox
            const filterTags = showGroupBookingOnly ? ["group"] : null;

            const data = await GetOrdersOfBuyer(baseUrl, "buyerToken", buyerToken, organizerId, false, filter, numberToSkip, filterTags);

            const ordersWithTickets = await attachTicketsToOrders(data.orderList);

            // Sort orders by dateCreated descending (latest first)
            const sortedOrders = ordersWithTickets.sort((a, b) => new Date(b.dateCreated) - new Date(a.dateCreated));

            if (numberToSkip && orders) {
                setOrders(prevOrders => [...prevOrders, ...sortedOrders]);
            } else {
                setOrders(sortedOrders);
            }

            setIsLoading(false);
        } catch (error) {
            setIsLoading(false);
            console.error('Error fetching buyer orders:', error);
        }
    };

    // Update count fetches to accept filterText
    const fetchCountOrders = async (filter = filterText) => {
        try {
            if (!buyerToken) {
                throw new Error("No buyerToken token found at fetchCountOrders");
            }
            // Apply tag filter based on showGroupBookingOnly checkbox
            const filterTags = showGroupBookingOnly ? ["group"] : null;

            const data = await GetCountOrders(baseUrl, "buyerToken", buyerToken, organizerId, false, filter, filterTags);
            setCountOrders(data);
        } catch (error) {
            console.error('Error fetching count orders:', error);
        }
    };

    const fetchCountBuyerOrders = async (filter = filterText) => {
        try {
            if (!buyerToken) {
                throw new Error("No buyerToken token found at fetchCountBuyerOrders");
            }
            // Apply tag filter based on showGroupBookingOnly checkbox
            const filterTags = showGroupBookingOnly ? ["group"] : null;

            const data = await CountBuyerOrders(baseUrl, "buyerToken", buyerToken, organizerId, false, filter, filterTags);
            setCountOrders(data);
        } catch (error) {
            console.error('Error fetching count buyer orders:', error);
        }
    };

    const fetchEventById = async (eventId) => {
        try {
            if (!regularToken) {
                throw new Error("No regularToken found at fetchEventById");
            }
            const res = await GetEventById(baseUrl, "token", regularToken, eventId);
            return res;
        } catch (error) {
            console.error('Error fetching event by Id:', error);
        }
    };

    const fetchPdfTicketsOfOrder = async (orderId) => {
        try {
            if (!buyerToken) {
                throw new Error("No buyerToken token found at fetchPdfTicketsOfOrder");
            }
            const pdfBlob = await GetPdfTicketsOfOrderByOrderId(baseUrl, "buyerToken", buyerToken, orderId, organizerId);
            const data = window.URL.createObjectURL(pdfBlob);
            var link = document.createElement("a");
            link.href = data;
            link.download = "Tickets.pdf";
            link.click();
        } catch (error) {
            console.error('Error getting pdf tickets:', error);
        }
    };

    const fetchPdfReceiptOfOrder = async (orderId) => {
        try {
            if (!buyerToken) {
                throw new Error("No buyerToken token found at fetchPdfReceiptOfOrder");
            }
            const pdfBlob = await GetReceiptOfOrderByOrderId(baseUrl, "buyerToken", buyerToken, orderId, organizerId);
            const data = window.URL.createObjectURL(pdfBlob);
            var link = document.createElement("a");
            link.href = data;
            link.download = "Tickets.pdf";
            link.click();
        } catch (error) {
            console.error('Error getting pdf tickets:', error);
        }
    };

    const handleNewBooking = async (email) => {
        sessionStorage.setItem("adminNewBookingUsername", email.trim());
        navigate(`/${language}/home`);
    };

    // Helper to format date string as DD.MM.YYYY HH:mm
    function formatDate(dateString) {
        if (!dateString) return "";
        const date = new Date(dateString);
        const pad = (n) => n.toString().padStart(2, '0');
        return `${pad(date.getDate())}.${pad(date.getMonth() + 1)}.${date.getFullYear()} ${pad(date.getHours())}:${pad(date.getMinutes())}`;
    }

    // Helper to format date string as DD.MM.YYYY (for comparison)
    function formatDateShort(dateString) {
        if (!dateString) return "";
        const date = new Date(dateString);
        const pad = (n) => n.toString().padStart(2, '0');
        return `${pad(date.getDate())}.${pad(date.getMonth() + 1)}.${pad(date.getFullYear())}`;
    }

    // Helper to convert YYYY-MM-DD to DD.MM.YYYY
    function convertInputDateToShort(dateStr) {
        if (!dateStr) return "";
        const [yyyy, mm, dd] = dateStr.split("-");
        if (!yyyy || !mm || !dd) return "";
        return `${dd}.${mm}.${yyyy}`;
    }

    // Combine filter logic for orders
    const filteredOrders = orders ? orders.filter(order => {
        // Filter by showPastOrders
        if (!showPastOrders && order.isPastOrder) return false;
        // Filter by date if set
        let filterDateValue = filterDateText.trim();
        if (filterDate && !filterDateValue) {
            filterDateValue = convertInputDateToShort(filterDate);
        }
        if (filterDateValue) {
            // Compare only the date part
            if (formatDateShort(order.dateCreated) !== filterDateValue) return false;
        }
        return true;
    }) : [];

    return (
        <>
            <Header language={language} pagePath="login" pageTitle={resources?.Account_LoginYourAccount}
                ticketCount={order?.tickets?.length || 0} />

            <div className="page-container">

                <div className="container wrapper">

                    {/* navigation */}
                    <div className="row mb-4">
                        <div className="col-6">
                            <button className="btn-custom btn-navigation" type="submit">
                                {resources?.Account_MyOrders}
                            </button>
                        </div>
                        <div className="col-6 text-end">
                            <button className="btn-custom transparent btn-navigation" type="submit"
                                onClick={() => navigate(`/${language}/profile`)}>
                                {resources?.Account_MyProfile}
                            </button>
                        </div>
                    </div>

                </div>
                <div className="container">
                    {/* Filter Textbox */}
                    {isAdmin &&
                        <div className="row mb-4 justify-content-center">
                            <div className="col-md-12 d-flex justify-content-center align-items-center">
                                <div style={{ position: "relative", width: "100%", maxWidth: 400 }}>
                                    <img
                                        src={searchSvg}
                                        alt="Search"
                                        style={{ position: "absolute", left: 16, top: "50%", transform: "translateY(-50%)", width: 25, height: 25, pointerEvents: "none" }}
                                    />
                                    <input
                                        type="text"
                                        className="form-control filter-textbox ps-5"
                                        value={filterText}
                                        onChange={e => setFilterText(e.target.value)}
                                        style={{ maxWidth: 400, borderRadius: "25px", paddingLeft: 44 }}
                                    />
                                </div>
                            </div>

                            <div className="col-md-12 mt-4 d-flex justify-content-center align-items-center">
                                <div className="ms-3 d-flex align-items-center">
                                    <input
                                        type="checkbox"
                                        id="showGroupBookingOnly"
                                        checked={showGroupBookingOnly}
                                        onChange={e => setShowGroupBookingOnly(e.target.checked)}
                                        style={{ marginRight: 6, cursor: "pointer" }}
                                    />
                                    <label htmlFor="showGroupBookingOnly" style={{ marginBottom: 0, cursor: "pointer" }}>{resources?.Account_MyOrderGroup}</label>
                                </div>

                                <div className="ms-3 d-flex align-items-center">
                                    <input
                                        type="checkbox"
                                        id="showPastOrders"
                                        checked={showPastOrders}
                                        onChange={e => setShowPastOrders(e.target.checked)}
                                        style={{ marginRight: 6, cursor: "pointer" }}
                                    />
                                    <label htmlFor="showPastOrders" style={{ marginBottom: 0, cursor: "pointer" }}>{resources?.Account_MyOrderShowPastOrders}</label>
                                </div>

                                {/* Date filter input */}
                                <div className="ms-3 d-flex align-items-center">
                                    <input
                                        type="date"
                                        className="form-control"
                                        style={{ maxWidth: 160, borderRadius: "25px" }}
                                        value={filterDate}
                                        onChange={e => {
                                            setFilterDate(e.target.value);
                                            setFilterDateText(""); // clear manual entry if date picked
                                        }}
                                        placeholder="Datum"
                                    />
                                </div>
                            </div>
                        </div>
                    }

                    {isLoading ? (
                        <div className="loading-wrapper">
                            <Loading
                                alignment="center"
                                color="#d3d3d3"
                                bgColor="transparent"
                                top="50%"
                                resources={resources}
                            />
                        </div>
                    ) : (
                        <>
                            {/* Orders Table */}
                            {filteredOrders && filteredOrders.length > 0 ? (
                                <>
                                    <table className="table table-no-border table-align-top">
                                        <thead>
                                            <tr>
                                                <th>{resources?.Account_MyOrdersDateTime}</th>
                                                <th>{resources?.Account_MyOrdersBuyer}</th>
                                                <th>{resources?.Account_MyOrdersInvoiceNumber}</th>
                                                <th>{resources?.Account_MyOrdersCurrency}</th>
                                                <th>{resources?.Account_MyOrdersTotal}</th>
                                                <th>{resources?.Account_MyOrdersIsPaid}</th>
                                                <th>{resources?.Account_MyProfileTicketsQuantity}</th>
                                                <th>{resources?.Account_MyOrdersTickets}</th>
                                                <th>{resources?.Account_MyOrdersReceipt}</th>
                                                <th>{resources?.Acount_MyOrdersView}</th>
                                                {isAdmin && <th>{resources?.Acount_MyOrdersNewBooking}</th>}
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {filteredOrders.map((order, idx) => (
                                                <tr key={order.invoiceNumber || idx}>
                                                    <td>{formatDate(order.dateCreated)}</td>
                                                    <td style={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{order.orderAddress?.firstName || ''} {order.orderAddress?.name || ''}</td>
                                                    <td>
                                                        {isAdmin ? (
                                                            <a
                                                                href={`https://cockpit.ticketino.com/${language}/CustomerOrders?organizerId=${organizerId}&orderId=${order.id}`}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                className="btn btn-link"
                                                                style={{ marginTop: "-3px", padding: 0, verticalAlign: "baseline" }}
                                                            >
                                                                {order.invoiceNumber}
                                                            </a>
                                                        ) : (
                                                            <span>{order.invoiceNumber}</span>
                                                        )}
                                                        {order.isCancelled &&
                                                            <p className="text-red">** {resources?._Cancelled} **</p>
                                                        }
                                                    </td>
                                                    <td>{order.currency}</td>
                                                    <td>{order.totalPrice.toFixed(2)}</td>
                                                    <td>{order.isPaid ? resources?._Yes : resources?._No}</td>
                                                    <td>{order.tickets?.length ?? ''}</td>
                                                    <td>
                                                        <button
                                                            className="btn btn-link"
                                                            type="button"
                                                            style={{ marginTop: "-3px" }}
                                                            disabled={order.isCancelled || (!isAdmin && !order.isOneWeekBeforeEvent) || (!isAdmin && order.paymentMethodId === 2) || (!isAdmin && !order.isPaid)}
                                                            onClick={() => fetchPdfTicketsOfOrder(order.id)}
                                                        >
                                                            {resources?._Download}
                                                        </button>
                                                    </td>
                                                    <td>
                                                        <button
                                                            className="btn btn-link"
                                                            type="button"
                                                            style={{ marginTop: "-3px" }}
                                                            disabled={order.isCancelled || (!isAdmin && !order.isOneWeekBeforeEvent) || (!isAdmin && order.paymentMethodId === 2) || (!isAdmin && !order.isPaid)}
                                                            onClick={() => fetchPdfReceiptOfOrder(order.id)}
                                                        >
                                                            {resources?._Download}
                                                        </button>
                                                    </td>
                                                    <td>
                                                        <button
                                                            type="button"
                                                            style={{ background: "none", border: "none", padding: 0, cursor: "pointer" }}
                                                            onClick={() => navigate(`/${language}/order-overview/${order.id}`)}
                                                            aria-label="Go to home"
                                                        >
                                                            <img src={previewSvg} alt="Go to order detail" style={{ width: 24, height: 24, marginTop: "-3px" }} />
                                                        </button>
                                                    </td>
                                                    {isAdmin &&
                                                        <td>
                                                            <button
                                                                type="button"
                                                                style={{ background: "none", border: "none", padding: 0, cursor: "pointer" }}
                                                                onClick={() => handleNewBooking(order.orderAddress?.email)}
                                                                aria-label="New booking"
                                                            >
                                                                <img src={newBookingSvg} alt="New booking" style={{ width: 24, height: 24, marginTop: "-4px" }} />
                                                            </button>
                                                        </td>
                                                    }
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                    {filteredOrders.length >= 100 && orders.length < countOrders &&
                                        <div className="row">
                                            <div className="col-md-12 text-center">
                                                <button className="btn-custom" onClick={() => {
                                                    if (isAdmin) {
                                                        fetchOrders(100);
                                                    } else {
                                                        fetchBuyerOrders(100);
                                                    }
                                                }}>{resources?._LoadMore}</button>
                                            </div>
                                        </div>
                                    }
                                </>
                            ) : (
                                <span>{resources?.Account_MyOrdersYouHaveNoOrders}</span>
                            )}
                        </>
                    )}
                </div>
            </div>

            <Footer language={language} />
        </>
    )
}

export default Orders